/**
 * The affiliate code of a register link (/register?ref=CODE).
 *
 * Remembered for 30 days, so a visitor who follows the link, leaves and registers
 * later from a plain /register is still attributed to the affiliate. A newer link
 * replaces the remembered code. Storage can be unavailable (private mode, blocked
 * site data): then only the code in the current URL counts.
 */
const STORAGE_KEY = 'schenkwerk_affiliate_ref';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const CODE_PATTERN = /^[A-Z0-9]{4,32}$/;

interface StoredRef {
  code: string;
  savedAt: number;
}

function normalize(raw: string | null | undefined): string | null {
  const code = (raw ?? '').trim().toUpperCase();
  return CODE_PATTERN.test(code) ? code : null;
}

/** Remembers the code from the URL, if any, and returns the code to register with. */
export function captureAffiliateRef(fromUrl: string | null | undefined): string | null {
  const code = normalize(fromUrl);
  if (code) {
    try {
      const value: StoredRef = { code, savedAt: Date.now() };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
      // Not remembered, but still used for this registration
    }
    return code;
  }
  return storedAffiliateRef();
}

function storedAffiliateRef(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as Partial<StoredRef>;
    if (typeof stored.savedAt !== 'number' || Date.now() - stored.savedAt > MAX_AGE_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return normalize(stored.code);
  } catch {
    return null;
  }
}

/** After a successful registration: the code has been recorded on the account. */
export function clearAffiliateRef(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear
  }
}
